import { useState } from "react";
import Table from "@cloudscape-design/components/table";
import Box from "@cloudscape-design/components/box";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Header from "@cloudscape-design/components/header";
import Button from "@cloudscape-design/components/button";
import Container from "@cloudscape-design/components/container";
import ColumnLayout from "@cloudscape-design/components/column-layout";
import { useTranslation } from "react-i18next";

export default function UsersList({ users, loading, onRefresh }) {
  const { t } = useTranslation();
  const [sortingColumn, setSortingColumn] = useState({ sortingField: "user_email" });
  const [sortingDescending, setSortingDescending] = useState(false);

  // Calculate totals
  const totalUsers = users.length;
  const totalFeeds = users.reduce((sum, user) => sum + user.feed_count, 0);
  const totalSlackIntegrations = users.reduce((sum, user) => sum + user.slack_enabled_feeds, 0);
  const totalNewsItems = users.reduce((sum, user) => sum + (user.news_items_count || 0), 0);

  // Sort users
  const sortedUsers = [...users].sort((a, b) => {
    const field = sortingColumn.sortingField || "user_email";
    const aValue = a[field] || "";
    const bValue = b[field] || "";

    // Handle numeric fields
    if (typeof aValue === "number" && typeof bValue === "number") {
      return sortingDescending ? bValue - aValue : aValue - bValue;
    }

    // Handle string fields
    const comparison = String(aValue).localeCompare(String(bValue), undefined, {
      numeric: true,
      sensitivity: "base",
    });
    return sortingDescending ? -comparison : comparison;
  });

  return (
    <SpaceBetween size="l">
      {/* Summary Box */}
      <Container header={<Header variant="h2">{t("admin.users.summary.title")}</Header>}>
        <ColumnLayout columns={4} variant="text-grid">
          <div>
            <Box variant="awsui-key-label">{t("admin.users.summary.total-users")}</Box>
            <Box variant="awsui-value-large">{totalUsers}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">{t("admin.users.summary.total-feeds")}</Box>
            <Box variant="awsui-value-large">{totalFeeds}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">{t("admin.users.summary.total-slack")}</Box>
            <Box variant="awsui-value-large">{totalSlackIntegrations}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">{t("admin.users.summary.total-news-items")}</Box>
            <Box variant="awsui-value-large">{totalNewsItems}</Box>
          </div>
        </ColumnLayout>
      </Container>

      {/* Users Table */}
      <Table
        loading={loading}
        loadingText={t("admin.users.loading")}
        sortingColumn={sortingColumn}
        sortingDescending={sortingDescending}
        onSortingChange={({ detail }) => {
          setSortingColumn(detail.sortingColumn);
          setSortingDescending(detail.isDescending);
        }}
        columnDefinitions={[
          {
            id: "user_email",
            header: t("admin.users.field.email"),
            cell: (item) => item.user_email,
            sortingField: "user_email",
          },
          {
            id: "feed_count",
            header: t("admin.users.field.feed-count"),
            cell: (item) => item.feed_count,
            sortingField: "feed_count",
          },
          {
            id: "enabled_feeds",
            header: t("admin.users.field.enabled-feeds"),
            cell: (item) => item.enabled_feeds,
            sortingField: "enabled_feeds",
          },
          {
            id: "slack_enabled_feeds",
            header: t("admin.users.field.slack-feeds"),
            cell: (item) => item.slack_enabled_feeds,
            sortingField: "slack_enabled_feeds",
          },
          {
            id: "news_items_count",
            header: t("admin.users.field.news-items"),
            cell: (item) => item.news_items_count || 0,
            sortingField: "news_items_count",
          },
          {
            id: "last_updated",
            header: t("admin.users.field.last-updated"),
            cell: (item) => (item.last_updated ? new Date(item.last_updated).toLocaleString() : "-"),
            sortingField: "last_updated",
          },
        ]}
        items={sortedUsers}
        header={
          <Header
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button iconName="refresh" onClick={onRefresh}>
                  {t("admin.action.refresh")}
                </Button>
              </SpaceBetween>
            }
          >
            {t("admin.users.title")}
          </Header>
        }
        empty={
          <Box textAlign="center" color="inherit">
            <b>{t("admin.users.no-users")}</b>
          </Box>
        }
      />
    </SpaceBetween>
  );
}
